import { axios, AxiosResponse } from "@sdk/axios";
import { SDKConfig } from "@sdk/default-sdk-config";
import type { PaginatedOptions, PaginatedResults } from "@sdk/utils/paginated-results";
import type { iLedgerTransaction } from "@sdk/ledger-transactions/ledger-transactions-models";

import type {
  iReconciliationException,
  iReconciliationMatch,
  iReconciliationSession,
  iStatementImport,
  iStatementImportLine,
  iStatementMappingTemplate,
  InternalRecordWithMatch,
  ReconciliationInternalRecordType,
  ReconciliationMatchSettings,
  ReconciliationSessionActivity,
  ReconciliationSessionExportType,
  ReconciliationType,
  StatementFieldMapping,
  StatementImportDetailResult,
  StatementImportPreviewResult,
} from "./reconciliation-models";

const RECONCILIATION_API_BASE = "/accounting/reconciliation";

function appendFormField(formData: FormData, key: string, value: unknown) {
  if (value == null || value === "") return;
  if (typeof value === "object") {
    formData.append(key, JSON.stringify(value));
    return;
  }
  formData.append(key, String(value));
}

function authHeaders(config: { token: string }) {
  return { Authorization: config.token };
}

export class ReconciliationServices {
  configure(config: { basePath?: string; token?: string }) {
    Object.assign(this.config, config);
  }

  constructor(protected config: { basePath: string; token: string }) {}

  get statementImports() {
    const { config } = this;
    return {
      preview: async (args: {
        file: File;
        headerRowIndex?: number;
        sampleSize?: number;
      }): Promise<StatementImportPreviewResult> => {
        const formData = new FormData();
        formData.append("file", args.file);
        appendFormField(formData, "headerRowIndex", args.headerRowIndex);
        appendFormField(formData, "sampleSize", args.sampleSize);

        const results: AxiosResponse<StatementImportPreviewResult> =
          await axios.post(
            config.basePath +
              `${RECONCILIATION_API_BASE}/statement-imports/preview`,
            formData,
            {
              headers: {
                ...authHeaders(config),
                "Content-Type": "multipart/form-data",
              },
            }
          );
        return results.data;
      },

      create: async (args: {
        file: File;
        reconciliationType:
          | "BANK_STATEMENT"
          | "CARD_STATEMENT"
          | "SUPPLIER_BILL_STATEMENT";
        fieldMapping: StatementFieldMapping;
        label?: string;
        targetLedgerId?: string;
        targetPaymentMethodId?: string;
        targetSupplierId?: string;
        statementPeriodStart?: number;
        statementPeriodEnd?: number;
        statementOpeningBalance?: number;
        statementClosingBalance?: number;
        currency?: string;
        headerRowIndex?: number;
        mappingTemplateId?: string;
      }): Promise<{ import: iStatementImport; stats: Record<string, unknown> }> => {
        const formData = new FormData();
        formData.append("file", args.file);
        appendFormField(formData, "reconciliationType", args.reconciliationType);
        appendFormField(formData, "fieldMapping", args.fieldMapping);
        appendFormField(formData, "label", args.label);
        appendFormField(formData, "targetLedgerId", args.targetLedgerId);
        appendFormField(
          formData,
          "targetPaymentMethodId",
          args.targetPaymentMethodId
        );
        appendFormField(formData, "targetSupplierId", args.targetSupplierId);
        appendFormField(
          formData,
          "statementPeriodStart",
          args.statementPeriodStart
        );
        appendFormField(formData, "statementPeriodEnd", args.statementPeriodEnd);
        appendFormField(
          formData,
          "statementOpeningBalance",
          args.statementOpeningBalance
        );
        appendFormField(
          formData,
          "statementClosingBalance",
          args.statementClosingBalance
        );
        appendFormField(formData, "currency", args.currency);
        appendFormField(formData, "headerRowIndex", args.headerRowIndex);
        appendFormField(formData, "mappingTemplateId", args.mappingTemplateId);

        const results: AxiosResponse<{
          import: iStatementImport;
          stats: Record<string, unknown>;
        }> = await axios.post(
          config.basePath + `${RECONCILIATION_API_BASE}/statement-imports`,
          formData,
          {
            headers: {
              ...authHeaders(config),
              "Content-Type": "multipart/form-data",
            },
          }
        );
        return results.data;
      },

      getById: async (id: string): Promise<StatementImportDetailResult> => {
        const results: AxiosResponse<StatementImportDetailResult> =
          await axios.get(
            config.basePath +
              `${RECONCILIATION_API_BASE}/statement-imports/${id}`,
            { headers: authHeaders(config) }
          );
        return results.data;
      },

      query: async (req: {
        query?: Record<string, unknown>;
        options?: PaginatedOptions;
      }): Promise<PaginatedResults<iStatementImport>> => {
        const results: AxiosResponse<PaginatedResults<iStatementImport>> =
          await axios.post(
            config.basePath +
              `${RECONCILIATION_API_BASE}/statement-imports/query`,
            req,
            { headers: authHeaders(config) }
          );
        return results.data;
      },
    };
  }

  get reconciliationSessions() {
    const { config } = this;
    return {
      create: async (body: {
        statementImportId: string;
        label?: string;
        periodStart: number;
        periodEnd: number;
        targetLedgerId?: string;
        targetPaymentMethodId?: string;
        targetSupplierId?: string;
        openingBalance?: number;
        closingBalance?: number;
        matchSettings?: Partial<ReconciliationMatchSettings>;
        runAutoMatch?: boolean;
      }): Promise<iReconciliationSession> => {
        const results: AxiosResponse<iReconciliationSession> = await axios.post(
          config.basePath + `${RECONCILIATION_API_BASE}/reconciliation-sessions`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      getById: async (id: string): Promise<iReconciliationSession> => {
        const results: AxiosResponse<iReconciliationSession> = await axios.get(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${id}`,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      query: async (req: {
        query?: Record<string, unknown>;
        options?: PaginatedOptions;
      }): Promise<PaginatedResults<iReconciliationSession>> => {
        const results: AxiosResponse<PaginatedResults<iReconciliationSession>> =
          await axios.post(
            config.basePath +
              `${RECONCILIATION_API_BASE}/reconciliation-sessions/query`,
            req,
            { headers: authHeaders(config) }
          );
        return results.data;
      },

      start: async (sessionId: string): Promise<iReconciliationSession> => {
        const results: AxiosResponse<iReconciliationSession> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/start`,
          {},
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      autoMatch: async (
        sessionId: string,
        body?: { preserveManualMatches?: boolean }
      ): Promise<iReconciliationSession> => {
        const results: AxiosResponse<iReconciliationSession> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/auto-match`,
          body ?? {},
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      refreshSummary: async (
        sessionId: string
      ): Promise<iReconciliationSession> => {
        const results: AxiosResponse<iReconciliationSession> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/summary`,
          {},
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      complete: async (sessionId: string): Promise<iReconciliationSession> => {
        const results: AxiosResponse<iReconciliationSession> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/complete`,
          {},
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      cancel: async (sessionId: string): Promise<iReconciliationSession> => {
        const results: AxiosResponse<iReconciliationSession> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/cancel`,
          {},
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      archive: async (sessionId: string): Promise<iReconciliationSession> => {
        const results: AxiosResponse<iReconciliationSession> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/archive`,
          {},
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      unarchive: async (sessionId: string): Promise<iReconciliationSession> => {
        const results: AxiosResponse<iReconciliationSession> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/unarchive`,
          {},
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      delete: async (
        sessionId: string
      ): Promise<{ id: string; deletedAt: number }> => {
        const results: AxiosResponse<{ id: string; deletedAt: number }> =
          await axios.delete(
            config.basePath +
              `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}`,
            { headers: authHeaders(config) }
          );
        return results.data;
      },

      reopen: async (sessionId: string): Promise<iReconciliationSession> => {
        const results: AxiosResponse<iReconciliationSession> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/reopen`,
          {},
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      queryStatementLines: async (
        sessionId: string,
        req: {
          query?: Record<string, unknown>;
          options?: PaginatedOptions;
        }
      ): Promise<PaginatedResults<iStatementImportLine>> => {
        const results: AxiosResponse<PaginatedResults<iStatementImportLine>> =
          await axios.post(
            config.basePath +
              `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/statement-lines/query`,
            req,
            { headers: authHeaders(config) }
          );
        return results.data;
      },

      queryInternalRecords: async (
        sessionId: string,
        req: {
          query?: Record<string, unknown>;
          options?: PaginatedOptions;
        }
      ): Promise<{
        docs: InternalRecordWithMatch[];
        totalDocs: number;
        page: number;
        limit: number;
        offset: number;
      }> => {
        const results: AxiosResponse<{
          docs: InternalRecordWithMatch[];
          totalDocs: number;
          page: number;
          limit: number;
          offset: number;
        }> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/internal-records/query`,
          req,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      queryExceptions: async (
        sessionId: string,
        req: {
          query?: Record<string, unknown>;
          options?: PaginatedOptions;
        }
      ): Promise<PaginatedResults<iReconciliationException>> => {
        const results: AxiosResponse<PaginatedResults<iReconciliationException>> =
          await axios.post(
            config.basePath +
              `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/exceptions/query`,
            req,
            { headers: authHeaders(config) }
          );
        return results.data;
      },

      createMatch: async (
        sessionId: string,
        body: {
          statementImportLineId: string;
          statementImportLineIds?: string[];
          internalRecordType?: ReconciliationInternalRecordType;
          internalRecordId: string;
          internalRecordIds?: string[];
          matchType?: "MANUAL" | "SPLIT" | "AUTO";
          forceMatch?: boolean;
          notes?: string;
        }
      ): Promise<iReconciliationMatch> => {
        const results: AxiosResponse<iReconciliationMatch> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/matches`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      unmatch: async (
        sessionId: string,
        matchId: string
      ): Promise<iReconciliationMatch> => {
        const results: AxiosResponse<iReconciliationMatch> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/matches/${matchId}/unmatch`,
          {},
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      createLedgerTransaction: async (
        sessionId: string,
        body: {
          statementImportLineId: string;
          ledgerId?: string;
          description?: string;
          occurredAt?: number;
          type?: "CREDIT" | "DEBIT";
          amount?: number;
          reference?: string;
          notes?: string;
        }
      ): Promise<{
        ledgerTransaction: iLedgerTransaction;
        match: iReconciliationMatch;
      }> => {
        const results: AxiosResponse<{
          ledgerTransaction: iLedgerTransaction;
          match: iReconciliationMatch;
        }> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/actions/create-ledger-transaction`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      createJournal: async (
        sessionId: string,
        body: {
          statementImportLineId?: string;
          statementImportLineIds?: string[];
          description: string;
          occurredAt: number;
          legs: Array<{
            ledgerId: string;
            type: "CREDIT" | "DEBIT";
            amount: number;
            description?: string;
          }>;
          notes?: string;
        }
      ): Promise<unknown> => {
        const results: AxiosResponse<unknown> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/actions/create-journal`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      markTiming: async (
        sessionId: string,
        body: {
          internalRecordType?: "LEDGER_TRANSACTION" | "SUPPLIER_BILL";
          internalRecordId: string;
          notes?: string;
        }
      ): Promise<unknown> => {
        const results: AxiosResponse<unknown> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/actions/mark-timing`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      ignoreLine: async (
        sessionId: string,
        body: {
          statementImportLineId: string;
          notes: string;
        }
      ): Promise<unknown> => {
        const results: AxiosResponse<unknown> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/actions/ignore-line`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      bulkPayBills: async (
        sessionId: string,
        body: import("@sdk/supplier-bills/supplier-bills.service").BulkPaySupplierBillsParams
      ) => {
        const results = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/bulk-pay-bills`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data as import("@sdk/supplier-bills/supplier-bills.service").BulkPaySupplierBillsResult;
      },

      postCardSettlement: async (
        sessionId: string,
        body: {
          statementImportLineId: string;
          bankLedgerId?: string;
          pdqCharges?: number;
          totalReceivedToBank?: number;
          description?: string;
          occurredAt?: number;
        }
      ) => {
        const results = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/actions/post-card-settlement`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data as {
          receiptIds: string[];
          journalEntryIds: string[];
          bankLedgerTransactionId?: string;
          pdqCharges: number;
          totalReceivedToBank: number;
        };
      },

      linkSupplierBill: async (
        sessionId: string,
        body: {
          statementImportLineId: string;
          supplierBillId: string;
          supplierInvoiceNumber?: string;
          notes?: string;
        }
      ) => {
        const results = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/actions/link-supplier-bill`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data as {
          match: iReconciliationMatch;
          supplierBill: Record<string, unknown>;
        };
      },

      flagMissingBill: async (
        sessionId: string,
        body: {
          statementImportLineId: string;
          notes: string;
          suggestedBookingRef?: string;
        }
      ) => {
        const results = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/actions/flag-missing-bill`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data as {
          statementImportLineId: string;
          exception: iReconciliationException;
        };
      },

      getActivity: async (
        sessionId: string
      ): Promise<ReconciliationSessionActivity[]> => {
        const results: AxiosResponse<ReconciliationSessionActivity[]> =
          await axios.get(
            config.basePath +
              `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/activity`,
            { headers: authHeaders(config) }
          );
        return results.data;
      },

      exportSession: async (
        sessionId: string,
        exportType: ReconciliationSessionExportType
      ): Promise<{ csv: string; filename: string; rowCount: number }> => {
        const results: AxiosResponse<{
          csv: string;
          filename: string;
          rowCount: number;
        }> = await axios.get(
          config.basePath +
            `${RECONCILIATION_API_BASE}/reconciliation-sessions/${sessionId}/export/${exportType}`,
          { headers: authHeaders(config) }
        );
        return results.data;
      },
    };
  }

  get statementMappingTemplates() {
    const { config } = this;
    return {
      query: async (req: {
        query?: Record<string, unknown>;
        options?: PaginatedOptions;
      }): Promise<PaginatedResults<iStatementMappingTemplate>> => {
        const results: AxiosResponse<PaginatedResults<iStatementMappingTemplate>> =
          await axios.post(
            config.basePath +
              `${RECONCILIATION_API_BASE}/statement-mapping-templates/query`,
            req,
            { headers: authHeaders(config) }
          );
        return results.data;
      },

      getById: async (id: string): Promise<iStatementMappingTemplate> => {
        const results: AxiosResponse<iStatementMappingTemplate> = await axios.get(
          config.basePath +
            `${RECONCILIATION_API_BASE}/statement-mapping-templates/${id}`,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      create: async (body: {
        name: string;
        reconciliationType: ReconciliationType;
        fieldMapping: StatementFieldMapping;
        isDefault?: boolean;
      }): Promise<iStatementMappingTemplate> => {
        const results: AxiosResponse<iStatementMappingTemplate> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/statement-mapping-templates`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      update: async (
        id: string,
        body: {
          name?: string;
          fieldMapping?: StatementFieldMapping;
          isDefault?: boolean;
        }
      ): Promise<iStatementMappingTemplate> => {
        const results: AxiosResponse<iStatementMappingTemplate> = await axios.patch(
          config.basePath +
            `${RECONCILIATION_API_BASE}/statement-mapping-templates/${id}`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      suggest: async (body: {
        reconciliationType: ReconciliationType;
        headers: string[];
      }): Promise<{
        suggestedTemplate: iStatementMappingTemplate | null;
        score: number;
        ranked: Array<{ template: iStatementMappingTemplate; score: number }>;
      }> => {
        const results: AxiosResponse<{
          suggestedTemplate: iStatementMappingTemplate | null;
          score: number;
          ranked: Array<{ template: iStatementMappingTemplate; score: number }>;
        }> = await axios.post(
          config.basePath +
            `${RECONCILIATION_API_BASE}/statement-mapping-templates/suggest`,
          body,
          { headers: authHeaders(config) }
        );
        return results.data;
      },

      delete: async (id: string): Promise<{ deleted: true }> => {
        const results: AxiosResponse<{ deleted: true }> = await axios.delete(
          config.basePath +
            `${RECONCILIATION_API_BASE}/statement-mapping-templates/${id}`,
          { headers: authHeaders(config) }
        );
        return results.data;
      },
    };
  }
}

export const reconciliationServices = new ReconciliationServices(SDKConfig);
